import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Switch,
  Table,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { axiosClient } from '@/api/axiosClient';
import { useGetFactoryList } from '@/modules/factory/hooks/useFactoryApi';

interface JobPostRow {
  id?: string;
  title: string;
  factory_id?: string;
  quantity: number;
  salary: number;
  description?: string;
  benefits?: string;
  requirements?: string;
  is_active: boolean;
}

// T-TT-01 — admin CRUD thông tin việc làm. App tab "Việc làm" của NLĐ
// lấy từ filter is_active=true.
const JobPostPage = () => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<JobPostRow | undefined>();
  const [form] = Form.useForm<JobPostRow>();
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery(
    ['job-posts'],
    () => axiosClient.get<{ rows: JobPostRow[] }>('/job-post/filter'),
    { refetchOnWindowFocus: false }
  );

  // V1.4 D4 — factory dropdown replaces the free-text Factory ID input.
  // Pull a wide page; modals don't paginate.
  const { data: factories } = useGetFactoryList({ limit: 200, page: 1 });
  const factoryOptions = useMemo(
    () =>
      ((factories?.rows as Array<{ id?: string; name: string }>) ?? [])
        .filter((f) => f.id)
        .map((f) => ({ label: f.name, value: f.id! })),
    [factories]
  );
  const factoryNameById = useMemo(() => {
    const m: Record<string, string> = {};
    ((factories?.rows as Array<{ id?: string; name: string }>) ?? []).forEach((f) => {
      if (f.id) m[f.id] = f.name;
    });

    return m;
  }, [factories]);

  // QC #56 — antd Form.validateFields only returns declared Form.Item values,
  // so `editing.id` was never propagated into the payload. Without `id`,
  // saveMutation took the create branch on every "Sửa" submit → duplicate
  // row. Inject the editing id explicitly here and forward it as the BE's
  // HandleUpdate reads ID from the request body.
  const saveMutation = useMutation(
    (payload: JobPostRow) =>
      payload.id
        ? axiosClient.patch(`/job-post/update/${payload.id}`, payload)
        : axiosClient.post('/job-post/create', payload),
    {
      onSuccess: () => {
        message.success('Đã lưu');
        setOpen(false);
        refetch();
        queryClient.invalidateQueries({ queryKey: ['job-posts'] });
      },
    }
  );

  const toggleMutation = useMutation(
    ({ id, active }: { id: string; active: boolean }) =>
      axiosClient.patch('/job-post/toggle', { active, id }),
    { onSuccess: () => refetch() }
  );

  useEffect(() => {
    if (open) {
      form.resetFields();
      if (editing) form.setFieldsValue(editing);
      else form.setFieldsValue({ is_active: true, quantity: 0, salary: 0, title: '' });
    }
  }, [open, editing, form]);

  const columns = [
    { dataIndex: 'title', key: 't', title: 'Tiêu đề' },
    {
      dataIndex: 'factory_id',
      key: 'f',
      render: (v?: string) => (v ? factoryNameById[v] ?? v.slice(0, 8) + '…' : '—'),
      title: 'Nhà máy',
    },
    { dataIndex: 'quantity', key: 'q', title: 'SL', width: 80 },
    { dataIndex: 'salary', key: 's', render: (v: number) => v.toLocaleString('vi-VN') + 'đ', title: 'Lương' },
    {
      dataIndex: 'is_active',
      key: 'a',
      render: (v: boolean, row: JobPostRow) => (
        <Switch
          checked={v}
          onChange={(c) => toggleMutation.mutate({ active: c, id: row.id! })}
          size="small"
        />
      ),
      title: 'Bật/Tắt',
      width: 90,
    },
    {
      key: 'act',
      render: (_: unknown, row: JobPostRow) => (
        <Button onClick={() => { setEditing(row); setOpen(true); }} size="small">
          Sửa
        </Button>
      ),
      title: '',
      width: 80,
    },
  ];

  return (
    <Card
      extra={
        <Button
          icon={<PlusOutlined rev="" />}
          onClick={() => { setEditing(undefined); setOpen(true); }}
          type="primary"
        >
          Tạo việc làm
        </Button>
      }
      title="Thông tin việc làm (T-TT-01)"
    >
      <Table<JobPostRow>
        columns={columns}
        dataSource={
          (data as unknown as { data?: { rows: JobPostRow[] } })?.data?.rows || []
        }
        loading={isLoading}
        rowKey="id"
      />
      <Modal
        cancelText="Huỷ"
        confirmLoading={saveMutation.isLoading}
        okText="Lưu"
        onCancel={() => setOpen(false)}
        onOk={async () => {
          const values = await form.validateFields();
          saveMutation.mutate(editing?.id ? { ...values, id: editing.id } : values);
        }}
        open={open}
        title={editing?.id ? 'Sửa việc làm' : 'Tạo việc làm'}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Tiêu đề" name="title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Nhà máy" name="factory_id">
            <Select
              allowClear
              optionFilterProp="label"
              options={factoryOptions}
              placeholder="Chọn nhà máy (để trống = chung cho mọi nhà máy)"
              showSearch
            />
          </Form.Item>
          <div className="row">
            <div className="col-md-6">
              <Form.Item label="Số lượng" name="quantity">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item label="Lương (VND)" name="salary">
                <InputNumber
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  min={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
          </div>
          <Form.Item label="Mô tả" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Quyền lợi" name="benefits">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Yêu cầu" name="requirements">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Đang hoạt động" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default JobPostPage;
